"use strict";

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";

import { AG_GRID_LOCALE_VN } from "@ag-grid-community/locale";
import { Box, Button, Grid, Group, Input, rem, Select } from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { useForm } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";
import { modals } from "@mantine/modals";
import { IconCalendarStats, IconFileExport, IconSearch, IconTimeDuration15, IconUser, IconUserCode } from "@tabler/icons-react";
import {
  ColDef,
  GridReadyEvent,
  ModuleRegistry
} from "ag-grid-community";
import {
  IServerSideDatasource,
  IServerSideGetRowsRequest,
  ServerSideRowModelModule
} from "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import { MemoryRouter } from "react-router-dom";
import { paginationPageSize, paginationPageSizeSelector, repositoryMdm, repositoryPos } from "../../../_base/_const/_constVar";
import BreadcrumbLayout1 from "../../../_base/component/_layout/_breadcrumb";
import { NotificationExtension } from "../../../_base/extension/NotificationExtension";
import { SelectListItemBase } from "../../../_base/model/_base/SelectListItemBase";
import { formatPrice } from "../../../common/formatPrice/PriceFunction";
import LoadingOverlayCustom from "../../../common/LoadingOverLayCustom/LoadingOverLayCustom";
import { BASE_API_MDM } from "../../../config";
import { MessageResponse } from "../../../model/MessageResponse";
import { SaleByStaffModel } from "../../../model/SalesByStaffModel";
import SalesRevenueReport from "../../Sell/SalesStatisticsList/SalesRevenueReport/SalesRevenueReport";
import { getTblDmEmployeeSelect } from "../../../service/getSelectApi";
import { ComboboxItem } from "../../../model/ComboboxItem";

ModuleRegistry.registerModules([
  ServerSideRowModelModule
]);

const SalesByStaff = () => {
  const [loading, setLoading] = useState(true);
  const gridRef = useRef<AgGridReact<any>>(null);
  const datasourceRef = useRef<IServerSideDatasource | null>(null);
  const [totalRow, setTotalRow] = useState<SaleByStaffModel | null>(null);
  const [height, setHeight] = useState(800);
  const [dataBranchCode, setDataBranchCode] = useState<SelectListItemBase[]>([]);
  const [selectedPeriod, setSelectedPeriod] = useState<string | null>(null);
  const [periodOptions, setPeriodOptions] = useState<{ value: string; label: string }[]>([]);
  const headerRef = React.useRef<HTMLDivElement>(null);
  const [visible, { toggle, close, open }] = useDisclosure(false);
  const [dataEmployee, setDataEmployee] = useState<ComboboxItem[]>([]);

  const containerStyle = useMemo(() => ({ width: "100%", height: height }), [height]);
  const gridStyle = useMemo(() => ({ height: "100%", width: "100%" }), []);
  const sizeGrid = { base: 6, sm: 3, md: 3, lg: 3, xl: 2 };

  useEffect(() => {
    const updateHeight = () => {
      const headerHeight = headerRef.current?.offsetHeight || 0;
      // 400 là tổng khoảng cách cần trừ (breadcrumb, filters, padding, pagination)
      const newHeight = window.innerHeight - (210 + headerHeight);
      setHeight(newHeight);
    };

    // Đợi DOM render xong rồi mới tính
    setTimeout(updateHeight, 0);
    window.addEventListener("resize", updateHeight);

    return () => {
      window.removeEventListener("resize", updateHeight);
    };
  }, []);

  // Chỗ này để làm lọc dùng form cho đỡ lag so với state
  const form = useForm<any>({
    initialValues: {
      KeySearch: null,
      // LoaiKy: "NAM", // Đặt mặc định là "NAM"
      // TuNgay: formatLocalDate(new Date(new Date().getFullYear(), 0, 1)), // Ngày 01/01 của năm hiện tại
      // DenNgay: formatLocalDate(new Date(new Date().getFullYear(), 11, 31)), // Ngày 31/12 của năm hiện tại
      LoaiKy: "THANG",
      TuNgay: formatLocalDate(new Date(new Date().getFullYear(), new Date().getMonth(), 1)), // Ngày 01 của tháng hiện tại
      DenNgay: formatLocalDate(new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0)), // Ngày cuối cùng của tháng hiện tại
      MaNhanVien: null,
      SortBy: "mA_NHAN_VIEN", // Tên trường sắp xếp
      SortDirection: "ASC" // ASC là tăng dần ,DESC là giảm dần
    },
  });
  function formatLocalDate(date: Date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  // Hàm tự động set ngày theo loại kỳ
  const handleLoaiKyChange = useCallback((value: string | null) => {
    form.setFieldValue("LoaiKy", value ?? "NGAY");
    setSelectedPeriod(null); // Reset selected period when LoaiKy changes

    const now = new Date();
    let tuNgay: Date;
    let denNgay: Date;
    let options: { value: string; label: string }[] = [];

    switch (value) {
      case "NGAY":
        tuNgay = now;
        denNgay = now;
        break;

      case "THANG":
        // Populate months for the current year
        for (let i = 0; i < 12; i++) {
          options.push({ value: String(i + 1), label: `Tháng ${i + 1}` });
        }
        setPeriodOptions(options);
        tuNgay = new Date(now.getFullYear(), now.getMonth(), 1);
        denNgay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        setSelectedPeriod(String(now.getMonth() + 1));
        break;

      case "QUY":
        // Populate quarters
        options.push(
          { value: "1", label: "Quý 1 (Tháng 1-3)" },
          { value: "2", label: "Quý 2 (Tháng 4-6)" },
          { value: "3", label: "Quý 3 (Tháng 7-9)" },
          { value: "4", label: "Quý 4 (Tháng 10-12)" }
        );
        setPeriodOptions(options);
        const currentMonth = now.getMonth();
        const quarter = Math.floor(currentMonth / 3) + 1;
        setSelectedPeriod(String(quarter));
        const quarterStartMonth = (quarter - 1) * 3;
        tuNgay = new Date(now.getFullYear(), quarterStartMonth, 1);
        denNgay = new Date(now.getFullYear(), quarterStartMonth + 3, 0);
        break;

      case "NAM":
        // Populate years (e.g., current year and past 5 years)
        for (let i = 0; i < 5; i++) {
          const year = now.getFullYear() - i;
          options.push({ value: String(year), label: `Năm ${year}` });
        }
        setPeriodOptions(options);
        tuNgay = new Date(now.getFullYear(), 0, 1);
        denNgay = new Date(now.getFullYear(), 11, 31);
        setSelectedPeriod(String(now.getFullYear()));
        break;

      default:
        tuNgay = now;
        denNgay = now;
    }

    form.setFieldValue("TuNgay", formatLocalDate(tuNgay));
    form.setFieldValue("DenNgay", formatLocalDate(denNgay));
  }, [form]);

  const handlePeriodSelectChange = useCallback((value: string | null) => {
    setSelectedPeriod(value);
    const loaiKy = form.getValues().LoaiKy;
    const now = new Date();
    let tuNgay: Date;
    let denNgay: Date;

    if (!value) return;

    switch (loaiKy) {
      case "THANG":
        const month = parseInt(value, 10) - 1; // months are 0-indexed
        tuNgay = new Date(now.getFullYear(), month, 1);
        denNgay = new Date(now.getFullYear(), month + 1, 0);
        break;
      case "QUY":
        const quarter = parseInt(value, 10);
        const quarterStartMonth = (quarter - 1) * 3;
        tuNgay = new Date(now.getFullYear(), quarterStartMonth, 1);
        denNgay = new Date(now.getFullYear(), quarterStartMonth + 3, 0);
        break;
      case "NAM":
        const year = parseInt(value, 10);
        tuNgay = new Date(year, 0, 1);
        denNgay = new Date(year, 11, 31);
        break;
      default:
        return;
    }

    form.setFieldValue("TuNgay", formatLocalDate(tuNgay));
    form.setFieldValue("DenNgay", formatLocalDate(denNgay));
  }, [form]);

  // Lấy danh sách chi nhánh
  const fetchBranchCode = async () => {
    try {
      const response = await repositoryPos.get<
        MessageResponse<SelectListItemBase[]>
      >(`api/v1/TblDmInventory/get-select-branch?auth=true`);
      if (response && response.success) {
        console.log("LoggResponse", response);
        const data = response.data;
        setDataBranchCode(data);
      }
    } catch (error) {
      console.error(error);
      return;
    }
  };


  const fetchData = async (request: IServerSideGetRowsRequest) => {
    console.log("Logggrequest", request);
    try {
      // Lấy page size hiện tại từ grid API
      const currentPageSize = gridRef.current?.api?.paginationGetPageSize() || paginationPageSize;
      const skip = request.startRow || 0;
      const take = currentPageSize;

      const keySearch = {
        KeySearch: form.getValues().KeySearch || "",
        LoaiKy: form.getValues().LoaiKy || "",
        TuNgay: form.getValues().TuNgay || "",
        DenNgay: form.getValues().DenNgay || "",
        MaNhanVien: form.getValues().MaNhanVien || "",
        SortBy: form.getValues().SortBy || "",
        SortDirection: form.getValues().SortDirection || ""
      };

      let url = `/api/v1/BaoCaoBanHangNhanVien/get-list?Skip=${skip}&Take=${take}`;
      Object.entries(keySearch).forEach(([key, value]) => {
        if (value) {
          url += `&${key}=${encodeURIComponent(value)}`;
        }
      });

      // console.log("API URL:", url, "Skip:", skip, "Take:", take, "PageSize:", currentPageSize);
      return await repositoryMdm.get<MessageResponse<SaleByStaffModel[]>>(url);
    } catch (error) {
      console.error(error);
    } finally {
    }
  };
  // Call api bên server
  const getServerSideDatasource = useCallback((): IServerSideDatasource => {
    return {
      getRows: async (params) => {
        console.log("getRows called with filters:", form.getValues());
        var response = await fetchData(params.request);
        console.log("Response data:", response);
        console.log("First item:", response?.data?.[0]);
        // adding delay to simulate real server call
        if (response && response.success) {
          // Tách row TOTAL ra khỏi data chính
          const allData = response.data || [];
          const totalRowData = allData.find(row => row.mA_NHAN_VIEN === "TOTAL");
          const normalRows = allData.filter(row => row.mA_NHAN_VIEN !== "TOTAL");

          // Lưu TOTAL row vào state
          if (totalRowData) {
            setTotalRow(totalRowData);
          }

          params.success({
            rowData: normalRows,
            rowCount: (response as any).totalCount ?? normalRows.length,
          });
          setLoading(false);
        } else {
          // inform the grid request failed
          params.fail();
          setLoading(false);
        }
      },
    };
  }, []);
  // Sau khi chọn tìm kiếm xong thì sẽ thực hiện click để call api này 
  const handleSearch = useCallback(() => {
    console.log("Search clicked with values:", form.getValues());
    setLoading(true);
    const api = gridRef.current?.api;
    if (!api) {
      console.log("API not ready");
      return;
    }

    // Refresh server-side cache để gọi lại getRows với filter mới
    (api as any).refreshServerSide({ purge: true });
  }, []);

  const onGridReady = useCallback((params: GridReadyEvent) => {
    // đăng datasource đúng để ag-Grid gọi getRows
    const datasource = getServerSideDatasource();
    datasourceRef.current = datasource;
    params.api.setGridOption('serverSideDatasource', datasource);
    console.log("Grid ready, datasource registered");
  }, [getServerSideDatasource]);

  const getRowId = useCallback((params: any) => {
    return params.data?.mA_NHAN_VIEN || Math.random().toString();
  }, []);

  const getRowStyle = useCallback((params: any) => {
    if (params.node.rowPinned) {
      return {
        backgroundColor: '#e7f5ff',
        fontWeight: 700,
        fontSize: '14px',
        borderTop: '3px solid #15aabf',
      };
    }
    return undefined;
  }, []);

  // Hàm sửa lý sự kiện sắp xếp
  const handleSortChanged = useCallback(() => {
    const columnState = gridRef.current?.api?.getColumnState() || [];
    console.log("LogggcolumnState", columnState);
    // tìm cột có sort (asc/desc)
    const sortedCol = columnState.find((c: any) => c.sort);
    if (sortedCol) {
      form.setFieldValue("SortBy", sortedCol.colId);
      form.setFieldValue("SortDirection", (sortedCol.sort || "").toUpperCase() === "ASC" ? "ASC" : "DESC");
    } else {
      form.setFieldValue("SortBy", null);
      form.setFieldValue("SortDirection", null);
    }
    // reload server-side data với sort mới
    gridRef.current?.api?.refreshServerSide?.({ purge: true });
  }, [form]);
  const filterParamsDefault = {
    buttons: ["clear", "apply"],
    debounceMs: 500,
  };

  const [columnDefs] = useState<ColDef[]>([
    {
      field: "mA_NHAN_VIEN", headerName: "Mã NV", width: 100, onCellDoubleClicked: (params) => {
        const cellValue = params.value;
        return onHanleOpenModalNavigate(cellValue);
      },
      cellStyle: { cursor: 'pointer' }
    },
    { field: "teN_NHAN_VIEN", headerName: "Tên nhân viên", width: 220 },
    {
      field: "sO_LUONG_DON_HANG_KY_NAY", headerName: "SL đơn (Kỳ này)", width: 160, sortable: true,
      valueFormatter: (p) => (p.value != null ? p.value : "")
    },
    {
      field: "doanH_THU_KY_NAY", headerName: "Doanh thu (Kỳ này)", width: 200,
      valueFormatter: (p) => (p.value != null ? formatPrice(p.value) : "")
    },
    {
      field: "giaM_GIA_KY_NAY", headerName: "Giảm giá (Kỳ này)", width: 180,
      valueFormatter: (p) => (p.value != null ? formatPrice(p.value) : "")
    },
    {
      field: "chieT_KHAU_KY_NAY", headerName: "Chiết khấu (Kỳ này)", width: 180,
      valueFormatter: (p) => (p.value != null ? formatPrice(p.value) : "")
    },
    {
      field: "saU_CK_GG_KY_NAY", headerName: "Sau CK/GG (Kỳ này)", width: 200,
      valueFormatter: (p) => (p.value != null ? formatPrice(p.value) : "")
    },
    {
      field: "tieN_VON_KY_NAY", headerName: "Tiền vốn (Kỳ này)", width: 200,
      valueFormatter: (p) => (p.value != null ? formatPrice(p.value) : "")
    },
    {
      field: "tieN_LAI_KY_NAY", headerName: "Tiền lãi (Kỳ này)", width: 200,
      valueFormatter: (p) => (p.value != null ? formatPrice(p.value) : "")
    },

    {
      field: "sO_LUONG_DON_HANG_KY_TRUOC", headerName: "SL đơn (Kỳ trước)", width: 150,
      valueFormatter: (p) => (p.value != null ? formatPrice(p.value) : "")
    },
    {
      field: "doanH_THU_KY_TRUOC", headerName: "Doanh thu (Kỳ trước)", width: 200,
      valueFormatter: (p) => (p.value != null ? formatPrice(p.value) : "")
    },
    {
      field: "giaM_GIA_KY_TRUOC", headerName: "Giảm giá (Kỳ trước)", width: 180,
      valueFormatter: (p) => (p.value != null ? formatPrice(p.value) : "")
    },
    {
      field: "chieT_KHAU_KY_TRUOC", headerName: "Chiết khấu (Kỳ trước)", width: 180,
      valueFormatter: (p) => (p.value != null ? formatPrice(p.value) : "")
    },
    {
      field: "saU_CK_GG_KY_TRUOC", headerName: "Sau CK/GG (Kỳ trước)", width: 200,
      valueFormatter: (p) => (p.value != null ? formatPrice(p.value) : "")
    },
    {
      field: "tieN_VON_KY_TRUOC", headerName: "Tiền vốn (Kỳ trước)", width: 200,
      valueFormatter: (p) => (p.value != null ? formatPrice(p.value) : "")
    },
    {
      field: "tieN_LAI_KY_TRUOC", headerName: "Tiền lãi (Kỳ trước)", width: 200,
      valueFormatter: (p) => (p.value != null ? formatPrice(p.value) : "")
    },
  ]);
  // onHanleOpenModalNavigate
  const onHanleOpenModalNavigate = (value: any) => {
    modals.openConfirmModal({
      title: 'Chi tiết doanh số tính lương hàng tháng theo nhân viên hưởng số',
      children: (
        <MemoryRouter initialEntries={[window.location.pathname]}>
          <SalesRevenueReport itemNV={value} />
        </MemoryRouter>
      ),
      size: '1400px',
      confirmProps: { display: "none" },
      cancelProps: { display: "none" },
    })
  }
  const exportDataExcel = async () => {
    open(); // Hiển thị loading
    try {
      // Lấy page hiện tại / pageSize giống fetchData
      const currentPageSize = gridRef.current?.api?.paginationGetPageSize() || paginationPageSize;
      const currentPage = gridRef.current?.api?.paginationGetCurrentPage?.() || 0;
      const skip = currentPage * currentPageSize;
      const take = currentPageSize;

      // Build same keySearch như fetchData (đảm bảo định dạng ngày YYYY-MM-DD)
      const formatValue = (v: any) => {
        if (!v) return "";
        if (v instanceof Date) return formatLocalDate(v);
        return String(v);
      };

      const keySearch = {
        KeySearch: form.getValues().KeySearch || "",
        LoaiKy: form.getValues().LoaiKy || "",
        TuNgay: formatValue(form.getValues().TuNgay),
        DenNgay: formatValue(form.getValues().DenNgay),
        MaNhanVien: form.getValues().MaNhanVien || "",
        SortBy: form.getValues().SortBy || "",
        SortDirection: form.getValues().SortDirection || ""
      };

      let url = `/api/v1/BaoCaoBanHangNhanVien/export-bao-cao-ban-hang-theo-nhan-vien?Skip=${skip}&Take=${take}`;
      Object.entries(keySearch).forEach(([key, value]) => {
        if (value) {
          url += `&${key}=${encodeURIComponent(value)}`;
        }
      });
      const _url = BASE_API_MDM + url;
      const fullUrl = new URL(_url);
      const token = localStorage.getItem("token");

      // Gọi API
      const response = await fetch(fullUrl.toString(), {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token?.replace(/"/g, "")}`,
          "Content-Type": "application/vnd.ms-excel",
        },
      });
      // Kiểm tra trạng thái HTTP
      if (!response.ok) {
        const errorResponse = await response.json(); // Parse lỗi từ API
        const errorMessage =
          errorResponse?.message || "Đã xảy ra lỗi khi xuất dữ liệu!";
        throw new Error(errorMessage); // Ném lỗi để xử lý trong `catch`
      }

      // Xử lý file Excel
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const filename = "bao_cao_ban_hang_theo_nhan-vien.xlsx"; // Tên mặc định nếu không có header
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (error: any) {
      console.error("Error:", error);
      // Hiển thị thông báo lỗi
      NotificationExtension.Fails(
        error.message || "Đã xảy ra lỗi không xác định!"
      );
    } finally {
      close(); // Đóng loading
    }
  };
  const defaultColDef = useMemo<ColDef>(() => {
    return {
      sortable: true,
      resizable: true,
    };
  }, []);

  //  Lấy danh sách nhân viên
  const getDataEmpSelect = async () => {
    const res = await getTblDmEmployeeSelect();
    setDataEmployee(
      res
        ?.filter(
          (item) =>
            item.att1 != null && item.text != null && item.att1 !== "-1"
        )
        ?.map((item) => ({
          value: item.att1,
          label: `${item.att1} - ${item.text} `,
        }))
    );
  };
  useEffect(() => {
    getDataEmpSelect();
  }, []);
  return (
    <Box pos={"relative"} style={containerStyle}>
      <LoadingOverlayCustom visible={visible} />
      <div
        style={{
          border: "1px solid #dee2e6",
          padding: "0px 10px",
          overflowX: "hidden",
        }}
      >
        <Group justify="space-between">
          <BreadcrumbLayout1></BreadcrumbLayout1>
          <Button
            leftSection={
              <IconFileExport style={{ width: rem(14), height: rem(14) }} />
            }
            onClick={() => {
              exportDataExcel();
            }}
          >
            Export Excel
          </Button>
        </Group>
      </div>
      <div style={{ marginBottom: 12 }}>
        <Group align="end" wrap="wrap">
          <Grid style={{ width: "100%" }} mt={10}>
            {/* <Grid.Col span={sizeGrid}>
              <Input placeholder="Từ khoá"
                leftSection={<IconSearch color="#15aabf" />}
                key={form.key("keySearch")}
                {...form.getInputProps("KeySearch")} />
            </Grid.Col> */}
            <Grid.Col span={sizeGrid}>
              <Select
                size="sm"
                placeholder="Chọn loại kỳ"
                leftSection={<IconCalendarStats size={20} color="#15aabf" />}
                data={[
                  { value: "NGAY", label: "Ngày" },
                  { value: "THANG", label: "Tháng" },
                  { value: "QUY", label: "Quý" },
                  { value: "NAM", label: "Năm" },
                ]}
                {...form.getInputProps("LoaiKy")}
                onChange={handleLoaiKyChange}
                styles={{
                  input: {
                    fontWeight: 600,
                    borderColor: '#15aabf',
                    borderWidth: '2px',
                  }
                }}
              />
            </Grid.Col>
            {form.values.LoaiKy !== "NGAY" && (
              <Grid.Col span={sizeGrid}>
                <Select
                  size="sm"
                  placeholder={form.values.LoaiKy === "THANG" ? "Chọn tháng" : form.values.LoaiKy === "QUY" ? "Chọn quý" : "Chọn năm"}
                  leftSection={<IconCalendarStats size={20} color="#15aabf" />}
                  data={periodOptions}
                  value={selectedPeriod}
                  onChange={handlePeriodSelectChange}
                  styles={{
                    input: {
                      fontWeight: 600,
                      borderColor: '#15aabf',
                      borderWidth: '2px',
                    }
                  }}
                />
              </Grid.Col>
            )}
            <Grid.Col span={sizeGrid}>
              <DatePickerInput
                value={form.values.TuNgay ? new Date(form.values.TuNgay) : null}
                onChange={(d) => form.setFieldValue("TuNgay", d ? d.toISOString().slice(0, 10) : null)}
                placeholder="Từ ngày"
                clearable
                size="sm"
                locale="vi"
                valueFormat="DD/MM/YYYY"
                leftSection={<IconTimeDuration15 size={20} color="#15aabf" />}
                styles={{
                  input: {
                    fontWeight: 600,
                    borderColor: '#15aabf',
                    borderWidth: '2px',
                  }
                }}
              />
            </Grid.Col>
            <Grid.Col span={sizeGrid}>
              <DatePickerInput
                value={form.values.DenNgay ? new Date(form.values.DenNgay) : null}
                onChange={(d) => form.setFieldValue("DenNgay", d ? d.toISOString().slice(0, 10) : null)}
                placeholder="Đến ngày"
                clearable
                size="sm"
                locale="vi"
                valueFormat="DD/MM/YYYY"
                leftSection={<IconTimeDuration15 size={20} color="#15aabf" />}
                styles={{
                  input: {
                    fontWeight: 600,
                    borderColor: '#15aabf',
                    borderWidth: '2px',
                  }
                }}
              />
            </Grid.Col>
            {/* <Grid.Col span={sizeGrid}>
              <Input placeholder="Mã nhân viên"
                leftSection={<IconUser color="#15aabf" />}
                key={form.key("MaNhanVien")}
                {...form.getInputProps("MaNhanVien")}
                styles={{
                  input: {
                    fontWeight: 600,
                    borderColor: '#15aabf',
                    borderWidth: '2px',
                  }
                }} />
            </Grid.Col> */}
            <Grid.Col span={sizeGrid}>
              <Select
                // key={dataEmployee.length}
                placeholder={"Chọn nhân viên"}
                searchable
                clearable
                leftSection={<IconUserCode color="#15aabf" />}
                comboboxProps={{
                  transitionProps: { transition: "pop", duration: 200 },
                }}
                data={dataEmployee
                  .map((ele) => ({
                    label: ele.label,
                    value: ele.value,
                  }))}
                nothingFoundMessage={"Loading..."}
                key={form.key("MaNhanVien")}
                {...form.getInputProps("MaNhanVien")}
                onClick={() => {
                  if (dataEmployee.length === 0) {
                    getDataEmpSelect();
                  }
                }}
              />
            </Grid.Col>
            <Grid.Col span={sizeGrid}>
              <Button
                leftSection={<IconSearch size={20} />}
                color="blue"
                variant="filled"
                onClick={handleSearch}
                styles={{
                  root: {
                    fontWeight: 600,
                  }
                }}
              >
                Tìm kiếm
              </Button>
            </Grid.Col>
          </Grid>
        </Group>
      </div>
      <div style={gridStyle} className={"ag-theme-quartz"}>
        <AgGridReact<any>
          ref={gridRef}
          loading={loading}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          rowModelType={"serverSide"}
          pagination={true}
          paginationPageSize={100}
          paginationPageSizeSelector={paginationPageSizeSelector}
          onSortChanged={handleSortChanged}
          onGridReady={onGridReady}
          getRowId={getRowId}
          getRowStyle={getRowStyle}
          localeText={AG_GRID_LOCALE_VN}
          pinnedBottomRowData={totalRow ? [totalRow] : []}
        />
      </div>
    </Box>
  );
};
export default SalesByStaff;
